/**
 * Hover-to-play video on desktop; in-view loop on touch / no-hover devices.
 * Used by attraction cards, food venue cards, and image grid sections.
 */
(function () {
  if (window.__hoverInviewVideoLoaded) return;
  window.__hoverInviewVideoLoaded = true;

  var hoverCapableMq = window.matchMedia('(hover: hover) and (pointer: fine)');
  var bindings = new Set();
  var mqListenerAttached = false;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function ensureMqListener() {
    if (mqListenerAttached) return;
    mqListenerAttached = true;
    hoverCapableMq.addEventListener('change', function () {
      bindings.forEach(function (binding) {
        binding.refreshMode();
      });
    });
  }

  function bindHoverInviewVideo(trigger, video, options) {
    options = options || {};
    var playingClass = options.playingClass || 'is-playing';
    var threshold = typeof options.threshold === 'number' ? options.threshold : 0.35;
    var controller = null;
    var observer = null;

    function play() {
      video.classList.add(playingClass);
      var promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () {});
      }
    }

    function pause() {
      video.classList.remove(playingClass);
      video.pause();
      video.currentTime = 0;
    }

    function clearMode() {
      if (controller) {
        controller.abort();
        controller = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    function refreshMode() {
      clearMode();
      pause();

      if (hoverCapableMq.matches) {
        controller = new AbortController();
        var signal = controller.signal;

        trigger.addEventListener('mouseenter', play, { signal: signal });
        trigger.addEventListener('mouseleave', pause, { signal: signal });
        trigger.addEventListener('focusin', play, { signal: signal });
        trigger.addEventListener('focusout', function (e) {
          if (!trigger.contains(e.relatedTarget)) pause();
        }, { signal: signal });
        return;
      }

      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) play();
            else pause();
          });
        },
        { threshold: threshold, rootMargin: '0px' }
      );
      observer.observe(trigger);
    }

    var binding = {
      refreshMode: refreshMode,
      destroy: function () {
        bindings.delete(binding);
        clearMode();
        pause();
      }
    };

    bindings.add(binding);
    ensureMqListener();
    refreshMode();

    return binding;
  }

  function destroyBinding(root, bindingKey) {
    if (root && root[bindingKey]) {
      root[bindingKey].destroy();
      root[bindingKey] = null;
    }
  }

  function initAttractionCard(card) {
    if (!card || card.getAttribute('data-attr-card-video-init') === '1') return;

    var inner = card.querySelector('.attraction-card__inner');
    var video = card.querySelector('.attraction-card__video');
    if (!inner || !video) return;
    if (prefersReducedMotion()) return;

    card.setAttribute('data-attr-card-video-init', '1');
    card._hoverInviewVideoBinding = bindHoverInviewVideo(inner, video);
  }

  function resetAttractionCard(card) {
    destroyBinding(card, '_hoverInviewVideoBinding');
    card.removeAttribute('data-attr-card-video-init');
    initAttractionCard(card);
  }

  function initFoodVenueCard(card) {
    if (!card || card.getAttribute('data-fv-card-video-init') === '1') return;

    var trigger = card.querySelector('.food-venues-grid__media-link');
    var video = card.querySelector('.food-venues-grid__video');
    if (!trigger || !video) return;
    if (prefersReducedMotion()) return;

    card.setAttribute('data-fv-card-video-init', '1');
    card._hoverInviewVideoBinding = bindHoverInviewVideo(trigger, video);
  }

  function resetFoodVenueCard(card) {
    destroyBinding(card, '_hoverInviewVideoBinding');
    card.removeAttribute('data-fv-card-video-init');
    initFoodVenueCard(card);
  }

  function initImageGrid(root) {
    if (!root || root.getAttribute('data-image-grid-video-init') === '1') return;
    if (prefersReducedMotion()) return;

    root.setAttribute('data-image-grid-video-init', '1');
    if (!root._hoverInviewVideoBindings) {
      root._hoverInviewVideoBindings = [];
    }

    root.querySelectorAll('.image-grid__item').forEach(function (item) {
      var video = item.querySelector('.image-grid__video');
      if (!video) return;

      root._hoverInviewVideoBindings.push(bindHoverInviewVideo(item, video));
    });
  }

  function resetImageGrid(root) {
    if (root && root._hoverInviewVideoBindings) {
      root._hoverInviewVideoBindings.forEach(function (binding) {
        binding.destroy();
      });
      root._hoverInviewVideoBindings = [];
    }
    root.removeAttribute('data-image-grid-video-init');
    initImageGrid(root);
  }

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-attr-card]').forEach(initAttractionCard);
    scope.querySelectorAll('[data-fv-card]').forEach(initFoodVenueCard);
    scope.querySelectorAll('[data-image-grid]').forEach(initImageGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-attr-card]').forEach(resetAttractionCard);
    e.target.querySelectorAll('[data-fv-card]').forEach(resetFoodVenueCard);
    var grid = e.target.querySelector('[data-image-grid]');
    if (grid) resetImageGrid(grid);
  });

  window.bindHoverInviewVideo = bindHoverInviewVideo;
})();
